/**
 * Tiny Web Audio sound bank — synth tones, no asset files.
 * AudioContext is created lazily on the first user interaction since browsers
 * block autoplay until then.
 */

let ctx: AudioContext | null = null;
let unlocked = false;

function getCtx(): AudioContext | null {
	if (!ctx) {
		const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
		if (!Ctor) return null;
		ctx = new Ctor();
	}
	return ctx;
}

function unlock() {
	if (unlocked) return;
	const c = getCtx();
	if (!c) return;
	if (c.state === "suspended") void c.resume();
	unlocked = true;
}

["pointerdown", "keydown", "touchstart"].forEach((ev) =>
	window.addEventListener(ev, unlock, { once: false, passive: true }),
);

function tone(freq: number, duration: number, type: OscillatorType = "sine", gain = 0.18, delay = 0) {
	const c = getCtx();
	if (!c) return;
	const t = c.currentTime + delay;
	const osc = c.createOscillator();
	const g = c.createGain();
	osc.type = type;
	osc.frequency.setValueAtTime(freq, t);
	g.gain.setValueAtTime(0, t);
	g.gain.linearRampToValueAtTime(gain, t + 0.01);
	g.gain.exponentialRampToValueAtTime(0.001, t + duration);
	osc.connect(g);
	g.connect(c.destination);
	osc.start(t);
	osc.stop(t + duration + 0.05);
}

function sweep(fromHz: number, toHz: number, duration: number, type: OscillatorType = "sine", gain = 0.18) {
	const c = getCtx();
	if (!c) return;
	const t = c.currentTime;
	const osc = c.createOscillator();
	const g = c.createGain();
	osc.type = type;
	osc.frequency.setValueAtTime(fromHz, t);
	osc.frequency.exponentialRampToValueAtTime(Math.max(1, toHz), t + duration);
	g.gain.setValueAtTime(0, t);
	g.gain.linearRampToValueAtTime(gain, t + 0.01);
	g.gain.exponentialRampToValueAtTime(0.001, t + duration);
	osc.connect(g);
	g.connect(c.destination);
	osc.start(t);
	osc.stop(t + duration + 0.05);
}

// Persistent ambient "factory" sound: a low stepper hum + filtered noise fan
// that fades in/out based on how many printers are running.
let ambientNodes: { hum: OscillatorNode; humGain: GainNode; fanGain: GainNode; fanSource: AudioBufferSourceNode } | null = null;
let ambientLevel = 0;

function ensureAmbient() {
	if (ambientNodes) return ambientNodes;
	const c = getCtx();
	if (!c) return null;

	const hum = c.createOscillator();
	hum.type = "sawtooth";
	hum.frequency.value = 110; // low stepper-motor whine
	const humGain = c.createGain();
	humGain.gain.value = 0;
	hum.connect(humGain);
	humGain.connect(c.destination);
	hum.start();

	// Pink-ish noise via a one-shot buffer played as a loop, low-passed.
	const noiseBuf = c.createBuffer(1, c.sampleRate * 2, c.sampleRate);
	const data = noiseBuf.getChannelData(0);
	for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
	const fanSource = c.createBufferSource();
	fanSource.buffer = noiseBuf;
	fanSource.loop = true;
	const lp = c.createBiquadFilter();
	lp.type = "lowpass";
	lp.frequency.value = 600;
	const fanGain = c.createGain();
	fanGain.gain.value = 0;
	fanSource.connect(lp);
	lp.connect(fanGain);
	fanGain.connect(c.destination);
	fanSource.start();

	ambientNodes = { hum, humGain, fanGain, fanSource };
	return ambientNodes;
}

export const sfx = {
	click() {
		tone(720, 0.06, "square", 0.08);
	},
	buy() {
		tone(523, 0.08, "triangle", 0.18);
		tone(784, 0.12, "triangle", 0.18, 0.06);
	},
	accept() {
		tone(440, 0.08, "triangle", 0.16);
		tone(660, 0.1, "triangle", 0.16, 0.05);
	},
	payout() {
		// Cha-ching
		tone(880, 0.1, "triangle", 0.22);
		tone(1320, 0.12, "triangle", 0.22, 0.07);
		tone(1760, 0.16, "triangle", 0.18, 0.16);
	},
	fail() {
		sweep(660, 220, 0.4, "sawtooth", 0.16);
	},
	repair() {
		tone(330, 0.06, "square", 0.12);
		tone(495, 0.08, "square", 0.12, 0.05);
		tone(660, 0.1, "triangle", 0.16, 0.1);
	},
	error() {
		sweep(440, 220, 0.2, "square", 0.15);
	},
	completionBeep() {
		// Three rising blips when a print finishes — distinct from the cha-ching payout.
		tone(880, 0.06, "square", 0.12);
		tone(1180, 0.06, "square", 0.12, 0.08);
		tone(1480, 0.1, "square", 0.14, 0.16);
	},
	/**
	 * Smoothly set the ambient hum + fan level (0..1). Call this every frame
	 * with the fraction of printers currently printing. Audio engine only
	 * spins up on first user interaction (unlock).
	 */
	setAmbientLevel(level: number) {
		ambientLevel = Math.max(0, Math.min(1, level));
		const a = ensureAmbient();
		const c = getCtx();
		if (!a || !c) return;
		const target = ambientLevel * 0.05; // keep it subtle
		const t = c.currentTime;
		a.humGain.gain.setTargetAtTime(target, t, 0.25);
		a.fanGain.gain.setTargetAtTime(target * 0.6, t, 0.25);
	},
};
